"use client"

import RightArrow from "@icons/RightArrow.svg?react"
import Gallery from "@icons/Gallery.svg?react"
import Image from "next/image"
import Link from "next/link"
import { motion } from "framer-motion"
import { useState, useRef, useEffect } from "react"
import clsx from "clsx/lite"
import { useSwipeable } from "react-swipeable"

export default ({
    images = [],
    start = 0,
    galleryHref = null,
    arrows = true,
    slidesToShow = 1,
    showGalleryButton = true,
    close = () => {},
}) => {
    const [active, setActive] = useState(start + slidesToShow)
    const [containerWidth, setContainerWidth] = useState(0)
    const [isAnimating, setIsAnimating] = useState(false)
    const containerRef = useRef(null)

    const swipeHandlers = useSwipeable({
        onSwipedLeft: () => next(),
        onSwipedRight: () => previous(),
    })

    const totalSlides = images.length
    const allSlides = [
        ...images.slice(-slidesToShow),
        ...images,
        ...images.slice(0, slidesToShow),
    ]

    useEffect(() => {
        const updateWidth = () => {
            if (containerRef.current) {
                setContainerWidth(containerRef.current.offsetWidth)
            }
        }

        updateWidth()
        window.addEventListener("resize", updateWidth)
        return () => window.removeEventListener("resize", updateWidth)
    }, [])

    const next = () => {
        if (isAnimating) return
        setIsAnimating(true)
        setActive((prev) => prev + 1)
        setTimeout(() => setIsAnimating(false), 500)
    }

    const previous = () => {
        if (isAnimating) return
        setIsAnimating(true)
        setActive((prev) => prev - 1)
        setTimeout(() => setIsAnimating(false), 500)
    }

    const slideWidth = containerWidth / slidesToShow

    const handleTransitionEnd = () => {
        setIsAnimating(false)

        if (active >= totalSlides + slidesToShow) {
            setActive(slidesToShow)
        } else if (active < slidesToShow) {
            setActive(totalSlides + slidesToShow - 1)
        }
    }

    useEffect(() => {
        galleryHref =
            galleryHref ??
            `${window.location.origin}${window.location.pathname}#gallery`
    }, [])

    return (
        <div
            className="w-full md:w-fit flex items-center h-full md:h-auto group relative"
            onClick={() => close()}
        >
            {arrows && (
                <div className="relative h-full grow">
                    <button
                        onClick={(e) => {
                            e.stopPropagation()
                            previous()
                        }}
                        className="hidden absolute pointer-fine:block opacity-0 group-hover:md:opacity-100 border-4 p-4 bg-white-snow/75 border-twilight-gray size-fit top-1/2 left-0 z-10 cursor-pointer transition-opacity -translate-y-1/2"
                    >
                        <RightArrow className="rotate-180 size-8 fill-sable" />
                    </button>
                </div>
            )}

            <div
                ref={containerRef}
                className={clsx(
                    "overflow-hidden w-fit h-auto shrink-0 md:aspect-video relative",
                    showGalleryButton ? "aspect-video" : "",
                )}
            >
                <motion.div
                    {...swipeHandlers}
                    className={clsx(
                        "flex h-full",
                        slidesToShow > 1 ? "gap-4" : "",
                    )}
                    initial={false}
                    animate={{ x: `-${(active * 100) / allSlides.length}%` }}
                    transition={{
                        duration: isAnimating ? 0.5 : 0,
                        ease: [0.4, 0, 0.2, 1],
                    }}
                    onAnimationComplete={() => handleTransitionEnd()}
                    style={{
                        width: `${(allSlides.length * 100) / slidesToShow + 1}%`,
                    }}
                    onClick={() => close()}
                >
                    {allSlides.map((image, idx) => (
                        <div
                            key={idx}
                            className="h-full w-full md:w-fit flexi my-auto"
                            style={{
                                width: `${100 / allSlides.length}%`,
                                flexShrink: 0,
                            }}
                        >
                            <Image
                                src={`${process.env.NEXT_PUBLIC_STATIC_PREFIX}${image.url}`}
                                width={image.width}
                                height={image.height}
                                alt={image.alt}
                                className="object-contain w-full md:w-fit h-full md:mx-auto"
                                priority={idx === slidesToShow}
                                onClick={(e) => {
                                    e.stopPropagation()
                                }}
                            />
                        </div>
                    ))}
                </motion.div>
            </div>
            {showGalleryButton && (
                <div className="absolute bottom-0 translate-y-[50%] left-[50%] -translate-x-[50%]">
                    <Link
                        href={galleryHref ?? "#gallery"}
                        className="bg-arugula w-fit px-6 py-2 font-bold inline-flex items-center hover:bg-garden-grove transition-colors group/gallery mx-auto md:mx-0 whitespace-nowrap"
                    >
                        View Gallery
                        <Gallery className="size-10 scale-90  fill-garden-grove group-hover/gallery:fill-arugula ml-4" />
                    </Link>
                </div>
            )}
            {arrows && (
                <div className="relative h-full grow">
                    <button
                        onClick={(e) => {
                            e.stopPropagation()
                            next()
                        }}
                        className="hidden pointer-fine:block opacity-0 group-hover:md:opacity-100 border-4 p-4 bg-white-snow/75 border-twilight-gray size-fit absolute top-1/2 right-0 z-20 cursor-pointer transition-opacity -translate-y-1/2"
                    >
                        <RightArrow className="size-8 fill-sable" />
                    </button>
                </div>
            )}
        </div>
    )
}
